// StageDropdownAtom.tsx - Reusable dropdown atom for stage selection

import React, { useRef, useEffect } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { StageDefinition } from 'types/arc';
import { useTranslation } from 'localization';
import './StageDropdownAtom.scss';

// Global reference to track the currently open overlay
let currentOpenOverlay: OverlayPanel | null = null;

interface StageInstance {
  sphereId: string;
  stageInstanceId: string;
  stageDefinitionId: string;
  arcId: string;
  status: 'active' | 'not_started' | 'completed';
  startedAt: string | null;
  completedAt: string | null;
}

export interface StageDropdownAtomProps {
  stages: StageDefinition[];
  stageInstances: StageInstance[];
  currentStageId: string;
  currentStageName: string;
  loading?: boolean;
  disabled?: boolean;
  requireComment?: boolean;
  onTransition: (targetStageId: string) => void;
  onRequestComment?: (targetStageId: string) => void;
  permissionCheck?: () => boolean | Promise<boolean>;
  className?: string;
  showChevron?: boolean;
}

const StageDropdownAtom: React.FC<StageDropdownAtomProps> = ({
  stages,
  stageInstances,
  currentStageId,
  currentStageName,
  loading = false,
  disabled = false,
  requireComment = false,
  onTransition,
  onRequestComment,
  permissionCheck,
  className = '',
  showChevron = true,
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<OverlayPanel>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // If this overlay is the currently open one, clear the reference
      if (currentOpenOverlay === overlayRef.current) {
        currentOpenOverlay = null;
      }
      overlayRef.current?.hide();
    };
  }, []);

  const getStageIcon = (stageDef?: StageDefinition, status?: StageInstance['status']) => {
    // Priority 1: Use icon from stage definition if specified
    if (stageDef?.icon) {
      return `pi pi-${stageDef.icon}`;
    }

    // Priority 2: Fallback to status-based icons
    if (status === 'completed') return 'pi pi-check-circle';
    if (status === 'active') return 'pi pi-play-circle';
    return 'pi pi-circle';
  };

  const currentStageInstance = stageInstances.find(si => si.stageDefinitionId === currentStageId);
  const currentStageDef = stages.find(s => s.id === currentStageId);

  const handleStageClick = (stageDef: StageDefinition, canTransition: boolean) => {
    if (!canTransition) return;

    // Close the dropdown after selection
    overlayRef.current?.hide();

    // Clear the global reference
    if (currentOpenOverlay === overlayRef.current) {
      currentOpenOverlay = null;
    }

    if (requireComment && onRequestComment) {
      onRequestComment(stageDef.id);
    } else {
      onTransition(stageDef.id);
    }
  };

  return (
    <div className={`stage-dropdown-atom ${className}`}>
      <button
        type="button"
        className="stage-selector-trigger"
        onClick={async (e) => {
          e.stopPropagation();
          if (disabled) return;

          // Check permissions before opening if permissionCheck is provided
          if (permissionCheck) {
            try {
              const hasPermission = await permissionCheck();
              if (!hasPermission) {
                return; // Don't open if no permission
              }
            } catch (error) {
              console.error('Permission check failed:', error);
              return;
            }
          }

          // Close any currently open overlay before opening this one
          if (currentOpenOverlay && currentOpenOverlay !== overlayRef.current) {
            currentOpenOverlay.hide();
          }

          // Toggle this overlay (onShow/onHide will handle tracking)
          overlayRef.current?.toggle(e);
        }}
        disabled={loading || disabled}
        style={disabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
      >
        {loading ? (
          <i className="pi pi-spin pi-spinner stage-icon" />
        ) : (
          <i className={`stage-icon ${getStageIcon(currentStageDef, currentStageInstance?.status || 'not_started')}`} />
        )}
        <span className="stage-name">{currentStageName}</span>
        {!loading && showChevron && <i className="pi pi-chevron-down" />}
      </button>

      {/* Dropdown overlay with all stages */}
      <OverlayPanel
        ref={overlayRef}
        className="arc-stage-dropdown"
        dismissable={true}
        showCloseIcon={false}
        appendTo={typeof document === 'undefined' ? undefined : document.body}
        onShow={() => {
          // Track this as the currently open overlay
          currentOpenOverlay = overlayRef.current;
        }}
        onHide={() => {
          // Clear the global reference when overlay is closed
          if (currentOpenOverlay === overlayRef.current) {
            currentOpenOverlay = null;
          }
        }}>
        <div className="stage-list">
          {stages.map((stageDef) => {
            const stageInstance = stageInstances.find(si => si.stageDefinitionId === stageDef.id);
            const isActive = stageInstance?.status === 'active';
            const stageName = t(stageDef.localizationKey, stageDef.fallbackName);

            // Check if this stage is an allowed target from current stage
            const currentStage = stages.find(s => s.id === currentStageId);
            const isAllowedTarget = currentStage?.transition?.allowedTargets?.includes(stageDef.id) || false;
            const canTransition = !isActive && isAllowedTarget;

            return (
              <button
                key={stageDef.id}
                type="button"
                className={`stage-item ${isActive ? 'active' : ''} ${canTransition ? 'clickable' : 'disabled'}`}
                onClick={() => handleStageClick(stageDef, canTransition)}
                disabled={!canTransition}
                title={!canTransition && !isActive ? t('arc.ui.cannot_transition_to_stage') : ''}
              >
                <div className="stage-info">
                  <i className={`stage-icon ${getStageIcon(stageDef, stageInstance?.status || 'not_started')}`} />
                  <span className="stage-name">{stageName}</span>
                </div>
              </button>
            );
          })}
        </div>
      </OverlayPanel>
    </div>
  );
};

export default StageDropdownAtom;